import React, {useEffect, useRef, useState} from 'react';
import {Button, Typography} from 'antd';
import {DownOutlined, RightOutlined, UpOutlined} from '@ant-design/icons';
import chatService from '../../services/chatService';
import type {AgentCallFlow, ChildCallInfo} from '../../types/chat';
import MarkdownRenderer from '../common/MarkdownRenderer';

const {Text} = Typography;

interface Props {
    sessionId: number;
    refreshTrigger: number;
    isPollingActive: boolean;
    onChildClick?: (childSessionId: number) => void;
}

const STATUS_CONFIG: Record<string, {
    color: string; bg: string; label: string;
}> = {
    SUBMITTED: {color: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.04)', label: '等待调度'},
    RUNNING: {color: 'var(--pt-primary)', bg: 'rgba(240,180,41,0.12)', label: '执行中'},
    PAUSED: {color: 'var(--pt-amber)', bg: 'rgba(250,140,22,0.12)', label: '等待恢复'},
    INTERRUPTED: {color: 'var(--pt-amber)', bg: 'rgba(250,140,22,0.12)', label: '已中断'},
    COMPLETED: {color: 'var(--pt-bull)', bg: 'rgba(0,200,150,0.1)', label: '已完成'},
    FAILED: {color: 'var(--pt-bear)', bg: 'rgba(255,107,107,0.1)', label: '失败'},
    TIMEOUT: {color: 'var(--pt-bear)', bg: 'rgba(255,107,107,0.1)', label: '超时'},
};

const MAIN_STATUS_CONFIG: Record<string, {
    color: string; bg: string; label: string;
}> = {
    SUSPENDED: {color: 'var(--pt-primary)', bg: 'rgba(240,180,41,0.12)', label: '挂起等待中'},
    EXECUTING: {color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', label: '执行中'},
    RESUMING: {color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', label: '恢复执行中'},
    COMPLETED: {color: 'var(--pt-bull)', bg: 'rgba(0,200,150,0.1)', label: '执行完成'},
    FAILED: {color: 'var(--pt-bear)', bg: 'rgba(255,107,107,0.1)', label: '执行失败'},
};

const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
};

const AgentCallFlowView: React.FC<Props> = ({
                                                sessionId, refreshTrigger, isPollingActive, onChildClick
                                            }) => {
    const [flowData, setFlowData] = useState<AgentCallFlow | null>(null);
    const [collapsed, setCollapsed] = useState(false);
    const prevStatusMap = useRef<Record<string, string>>({});
    const [expandedCallId, setExpandedCallId] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        chatService.getAgentCallFlow(sessionId).then(data => {
            if (cancelled) return;
            data.childCalls?.forEach(call => {
                prevStatusMap.current[call.callId] = call.status;
            });
            setFlowData(data);
        }).catch(() => {
        });
        return () => {
            cancelled = true;
        };
    }, [sessionId, refreshTrigger]);

    const hasData = flowData?.mainAgent && flowData.childCalls.length > 0;
    const isTerminal = flowData?.mainAgent?.status === 'COMPLETED'
        || flowData?.mainAgent?.status === 'FAILED';
    const [autoHidden, setAutoHidden] = useState(false);

    useEffect(() => {
        if (isTerminal && !autoHidden) {
            const timer = setTimeout(() => setAutoHidden(true), 3000);
            return () => clearTimeout(timer);
        }
    }, [isTerminal, autoHidden]);

    if (!hasData || autoHidden) return null;

    const {mainAgent, childCalls} = flowData!;
    const mainCfg = MAIN_STATUS_CONFIG[mainAgent!.status] || MAIN_STATUS_CONFIG.SUSPENDED;

    return (
        <div style={{
            border: '1px solid rgba(240,180,41,0.2)',
            borderRadius: 12,
            background: 'rgba(240,180,41,0.02)',
            marginTop: 12,
            marginBottom: 8,
            overflow: 'hidden'
        }}>
            <style>{`
                @keyframes flow-node-pulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(240,180,41,0.4); }
                    50% { box-shadow: 0 0 0 6px rgba(240,180,41,0); }
                }
                @keyframes flow-dot-pulse {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.3); opacity: 0.6; }
                }
            `}</style>

            {/* 标题栏 */}
            <div
                onClick={() => setCollapsed(!collapsed)}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', cursor: 'pointer',
                    borderBottom: collapsed ? 'none' : '1px solid rgba(255,255,255,0.06)',
                    transition: 'background 0.2s'
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                }}
            >
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <span style={{fontSize: 14}}>🔀</span>
                    <Text style={{color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 600}}>
                        智能体调用流程
                    </Text>
                    {isPollingActive && (
                        <span style={{
                            width: 6, height: 6, borderRadius: '50%',
                            background: 'var(--pt-primary)',
                            animation: 'flow-dot-pulse 1.5s ease-in-out infinite'
                        }}/>
                    )}
                </div>
                {collapsed ? <UpOutlined style={{color: 'rgba(255,255,255,0.45)', fontSize: 12}}/>
                    : <DownOutlined style={{color: 'rgba(255,255,255,0.45)', fontSize: 12}}/>}
            </div>

            {/* 流程图内容 */}
            {!collapsed && (
                <div style={{padding: '14px'}}>
                    {/* 主智能体节点 */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px', borderRadius: 10,
                        background: mainCfg.bg,
                        border: `1px solid ${mainCfg.color}40`,
                        marginBottom: 12
                    }}>
                        <div style={{
                            width: 10, height: 10, borderRadius: '50%',
                            background: mainCfg.color,
                            animation: isPollingActive ? 'flow-dot-pulse 1.5s ease-in-out infinite' : 'none'
                        }}/>
                        <div style={{flex: 1, minWidth: 0}}>
                            <Text style={{
                                fontSize: 12, fontWeight: 600,
                                color: 'rgba(255,255,255,0.9)', display: 'block'
                            }}>
                                🧠 {mainAgent!.agentName || '主智能体'}
                            </Text>
                        </div>
                        <span style={{
                            fontSize: 11, color: mainCfg.color,
                            padding: '2px 8px', borderRadius: 10,
                            background: mainCfg.bg, whiteSpace: 'nowrap'
                        }}>
                            {mainCfg.label}
                        </span>
                    </div>

                    {/* 连线 */}
                    {childCalls.length > 0 && (
                        <div style={{
                            display: 'flex', justifyContent: 'center', marginBottom: 8
                        }}>
                            <div style={{
                                width: 2, height: 20,
                                background: 'rgba(240,180,41,0.3)',
                                borderRadius: 1
                            }}/>
                        </div>
                    )}

                    {/* 子智能体节点列表 */}
                    <div style={{
                        display: 'flex', flexDirection: 'column', gap: 8,
                        paddingLeft: 16
                    }}>
                        {childCalls.map((child: ChildCallInfo) => {
                            const cfg = STATUS_CONFIG[child.status] || STATUS_CONFIG.SUBMITTED;
                            const isRunning = 'RUNNING' === child.status;
                            const isExpanded = expandedCallId === child.callId;
                            return (
                                <div key={child.callId}>
                                    {/* 子智能体节点 */}
                                    <div
                                        onClick={() => setExpandedCallId(isExpanded ? null : child.callId)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '8px 12px', borderRadius: 8,
                                            background: cfg.bg,
                                            border: `1px solid ${cfg.color}40`,
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            animation: isRunning ? 'flow-node-pulse 2s ease-in-out infinite' : 'none'
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.borderColor = cfg.color;
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.borderColor = `${cfg.color}40`;
                                        }}
                                    >
                                        {/* 状态点 */}
                                        <div style={{
                                            width: 8, height: 8, borderRadius: '50%',
                                            background: cfg.color,
                                            flexShrink: 0,
                                            animation: isRunning ? 'flow-dot-pulse 1.5s ease-in-out infinite' : 'none'
                                        }}/>

                                        {/* 名称和耗时 */}
                                        <div style={{flex: 1, minWidth: 0}}>
                                            <Text style={{
                                                fontSize: 12, color: 'rgba(255,255,255,0.85)',
                                                display: 'block', whiteSpace: 'nowrap',
                                                overflow: 'hidden', textOverflow: 'ellipsis'
                                            }}>
                                                {child.agentName || `智能体-${child.agentId}`}
                                            </Text>
                                            {null != child.durationMs && (
                                                <Text style={{fontSize: 10, color: 'rgba(255,255,255,0.35)'}}>
                                                    {formatDuration(child.durationMs)}
                                                </Text>
                                            )}
                                        </div>

                                        {/* 状态标签 */}
                                        <span style={{
                                            fontSize: 10, color: cfg.color,
                                            padding: '2px 6px', borderRadius: 8,
                                            background: cfg.bg, whiteSpace: 'nowrap',
                                            flexShrink: 0
                                        }}>
                                            {cfg.label}
                                        </span>

                                        {/* 展开/收起指示 */}
                                        <span style={{
                                            fontSize: 10, color: 'rgba(255,255,255,0.35)',
                                            transition: 'transform 0.2s'
                                        }}>
                                            {isExpanded ? <DownOutlined style={{fontSize: 10}}/>
                                                : <RightOutlined style={{fontSize: 10}}/>}
                                        </span>
                                    </div>

                                    {/* 展开详情面板 */}
                                    {isExpanded && (
                                        <div style={{
                                            marginTop: 4, marginBottom: 4, marginLeft: 8,
                                            padding: '10px 12px', borderRadius: 8,
                                            background: 'rgba(255,255,255,0.02)',
                                            border: '1px solid rgba(255,255,255,0.06)',
                                            borderLeft: `2px solid ${cfg.color}60`,
                                            animation: 'fadeIn 0.2s ease'
                                        }}>
                                            <style>{`
                                                @keyframes fadeIn {
                                                    from { opacity: 0; transform: translateY(-4px); }
                                                    to { opacity: 1; transform: translateY(0); }
                                                }
                                            `}</style>

                                            {/* 执行摘要 */}
                                            {child.summary && (
                                                <div style={{marginBottom: 10}}>
                                                    <Text style={{
                                                        fontSize: 11, color: 'rgba(255,255,255,0.55)',
                                                        display: 'block', marginBottom: 4
                                                    }}>执行结果</Text>
                                                    <div style={{
                                                        fontSize: 12, color: 'rgba(255,255,255,0.8)',
                                                        lineHeight: '1.6'
                                                    }}>
                                                        <MarkdownRenderer content={child.summary}/>
                                                    </div>
                                                </div>
                                            )}

                                            {/* 时间线 */}
                                            <div style={{marginBottom: null != child.durationMs ? 6 : 0}}>
                                                <Text style={{
                                                    fontSize: 10, color: 'rgba(255,255,255,0.35)',
                                                    display: 'block', marginBottom: 4
                                                }}>时间线</Text>
                                                <div style={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                                    <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                                        <div style={{
                                                            width: 6, height: 6, borderRadius: '50%',
                                                            background: 'rgba(255,255,255,0.2)', flexShrink: 0
                                                        }}/>
                                                        <Text style={{fontSize: 10, color: 'rgba(255,255,255,0.45)'}}>
                                                            提交: {child.submittedAt || '-'}
                                                        </Text>
                                                    </div>
                                                    {child.startedAt && (
                                                        <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                                            <div style={{
                                                                width: 6, height: 6, borderRadius: '50%',
                                                                background: 'var(--pt-primary)', flexShrink: 0
                                                            }}/>
                                                            <Text
                                                                style={{fontSize: 10, color: 'rgba(255,255,255,0.45)'}}>
                                                                开始: {child.startedAt}
                                                            </Text>
                                                        </div>
                                                    )}
                                                    {child.finishedAt && (
                                                        <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                                            <div style={{
                                                                width: 6, height: 6, borderRadius: '50%',
                                                                background: 'var(--pt-bull)', flexShrink: 0
                                                            }}/>
                                                            <Text
                                                                style={{fontSize: 10, color: 'rgba(255,255,255,0.45)'}}>
                                                                完成: {child.finishedAt}
                                                            </Text>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* 执行耗时 */}
                                            {null != child.durationMs && (
                                                <div style={{marginBottom: child.childSessionId ? 10 : 0}}>
                                                    <Text style={{
                                                        fontSize: 10, color: 'rgba(255,255,255,0.35)',
                                                        display: 'block', marginBottom: 2
                                                    }}>执行耗时</Text>
                                                    <Text style={{
                                                        fontSize: 12, color: 'var(--pt-primary)', fontWeight: 500
                                                    }}>
                                                        ⏱ {formatDuration(child.durationMs)}
                                                    </Text>
                                                </div>
                                            )}

                                            {/* 查看完整对话按钮 */}
                                            {child.childSessionId && onChildClick && (
                                                <Button
                                                    size="small"
                                                    type="link"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onChildClick(child.childSessionId!);
                                                    }}
                                                    style={{
                                                        padding: 0, height: 'auto', fontSize: 11,
                                                        color: 'var(--pt-primary)'
                                                    }}>
                                                    查看完整对话 →
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgentCallFlowView;
